import React, { useMemo, useState, useEffect } from 'react'
import { get } from 'lodash'
import { Box, Paper, Typography } from '@mui/material_modern'
import ButtonList from 'components/generic/buttons/ButtonList'
import BasicDataGridV2 from 'components/generic/datagrid/BasicDataGridV2'
import { useSearchBarV2 } from 'utils/custom-hooks/useSearchBarV2'
import { PAGE_SIZE } from 'utils/constants'

interface DataGridWithSearchV2Props {
  textSearchPlaceholder?: string
  rows: Array<any>
  columns: Array<any>
  width?: string
  height?: string
  showActionBar?: boolean
  actionBarContent?: React.ReactNode
  showFilterChips?: boolean
  filterChips?: React.ReactNode
  renderViewModeToggle?: () => React.ReactNode
  renderDateRangePicker?: () => React.ReactNode
  actionButtons?: Array<any>
  customPageSize?: number
  useBackendSortAndFilter?: boolean
  filter?: Record<string, any>
  allowedOptions?: Array<string>
  renderSelectFilter?: () => React.ReactNode
  selectFilterPath?: string
  centerContent?: string
  additionalFilterOptions?: Array<any>
  hideClearButton?: boolean
  onReset?: () => void
  defaultFilter?: Record<string, any>
  loading?: boolean
  searchBarSize?: 'small' | 'medium'
  [key: string]: any
}

const DataGridWithSearchV2: React.FC<DataGridWithSearchV2Props> = ({
  textSearchPlaceholder = 'Search all fields',
  rows,
  columns,
  width,
  height,
  showActionBar = false,
  actionBarContent = null,
  showFilterChips = false,
  filterChips = null,
  renderViewModeToggle = () => null,
  renderDateRangePicker = () => null,
  renderSelectFilter = () => null,
  selectFilterPath = '',
  actionButtons = [],
  customPageSize = PAGE_SIZE,
  useBackendSortAndFilter = false,
  filter = {},
  allowedOptions = [],
  centerContent = '',
  additionalFilterOptions = [],
  hideClearButton = false,
  onReset,
  defaultFilter = { search: '' },
  loading = false,
  searchBarSize = 'medium',
  ...restProps
}) => {
  const { filteredRows, renderSearchBar } = useSearchBarV2({
    textSearchPlaceholder,
    rows,
    showFilterChips,
    filterChips,
    renderViewModeToggle,
    renderDateRangePicker,
    renderSelectFilter,
    useBackendSortAndFilter,
    filter,
    additionalFilterOptions,
    hideClearButton,
    onReset,
    defaultFilter,
    searchBarSize,
    ...restProps,
  })

  // extra filter for showing/hiding statuses
  const tableRows = useMemo(
    () =>
      selectFilterPath
        ? filteredRows.filter(row => allowedOptions.includes(get(row, selectFilterPath)?.toUpperCase()))
        : filteredRows,
    [filteredRows, allowedOptions],
  )
  const [pageSize, setPageSize] = useState(customPageSize)

  return (
    <Paper elevation={0} sx={{ width, height, backgroundColor: 'background.transparent' }} data-testid="search-grid">
      <Box
        mb={2}
        sx={{
          display: 'flex',
          justifyContent: centerContent ? 'left' : 'space-between',
          alignItems: 'center',
        }}
      >
        {renderSearchBar()}
        <Typography sx={{ margin: '16px' }}>{centerContent}</Typography>
        <ButtonList buttonsData={actionButtons} />
      </Box>
      {showActionBar ? actionBarContent : null}
      <BasicDataGridV2
        pageSize={pageSize}
        onPageSizeChange={newPageSize => setPageSize(newPageSize)}
        rowsPerPageOptions={[10, 20, 30]}
        pagination
        autoHeight
        rows={tableRows}
        columns={columns}
        loading={loading}
        disableColumnMenu={useBackendSortAndFilter}
        {...restProps}
      />
    </Paper>
  )
}

export default DataGridWithSearchV2
